// Lossless mp3 trimming by frame-boundary cutting.
//
// An mp3 file is a sequence of self-contained frames (~26ms each), so the
// stream can be cut at any frame boundary without decoding or re-encoding.
// Used when saving a create-action project to the device: the audio written
// to the SD card is cut at the last action marker so the device stops the
// music where the actions end.

// bitrate tables in kbps, indexed by the header's 4-bit bitrate index (1..14)
const BITRATES = {
    // MPEG1
    1: {
        1: [0, 32, 64, 96, 128, 160, 192, 224, 256, 288, 320, 352, 384, 416, 448],
        2: [0, 32, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 384],
        3: [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320],
    },
    // MPEG2 and MPEG2.5
    2: {
        1: [0, 32, 48, 56, 64, 80, 96, 112, 128, 144, 160, 176, 192, 224, 256],
        2: [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160],
        3: [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160],
    },
}

const SAMPLE_RATES = {
    3: [44100, 48000, 32000], // MPEG1
    2: [22050, 24000, 16000], // MPEG2
    0: [11025, 12000, 8000], // MPEG2.5
}

// Parses the 4-byte frame header at `offset`.
// Returns { frameSize, frameSeconds } or null if it is not a valid header.
function parseFrameHeader(bytes, offset) {
    if (offset + 4 > bytes.length) return null
    if (bytes[offset] !== 0xff || (bytes[offset + 1] & 0xe0) !== 0xe0)
        return null

    const versionBits = (bytes[offset + 1] >> 3) & 0x03 // 0=MPEG2.5, 2=MPEG2, 3=MPEG1
    const layerBits = (bytes[offset + 1] >> 1) & 0x03 // 1=III, 2=II, 3=I
    const bitrateIndex = (bytes[offset + 2] >> 4) & 0x0f
    const sampleRateIndex = (bytes[offset + 2] >> 2) & 0x03
    const padding = (bytes[offset + 2] >> 1) & 0x01

    if (versionBits === 1 || layerBits === 0) return null
    if (bitrateIndex === 0 || bitrateIndex === 15) return null // free/invalid
    if (sampleRateIndex === 3) return null

    const isMpeg1 = versionBits === 3
    const layer = 4 - layerBits // 1, 2 or 3
    const bitrate =
        BITRATES[isMpeg1 ? 1 : 2][layer][bitrateIndex] * 1000
    const sampleRate = SAMPLE_RATES[versionBits][sampleRateIndex]

    let frameSize
    let samplesPerFrame
    if (layer === 1) {
        samplesPerFrame = 384
        frameSize = (Math.floor((12 * bitrate) / sampleRate) + padding) * 4
    } else {
        samplesPerFrame = layer === 2 || isMpeg1 ? 1152 : 576
        frameSize =
            Math.floor((samplesPerFrame / 8) * (bitrate / sampleRate)) +
            padding
    }
    if (frameSize <= 4) return null

    return { frameSize, frameSeconds: samplesPerFrame / sampleRate }
}

// Returns the byte offset of the first audio frame (skipping an ID3v2 tag),
// or -1 if none is found. A candidate sync is only accepted when the frame
// size it implies lands on another valid header (or the end of the file),
// which filters out false 0xFF syncs inside tag data.
function findFirstFrame(bytes) {
    let offset = 0
    if (
        bytes.length > 10 &&
        bytes[0] === 0x49 && // I
        bytes[1] === 0x44 && // D
        bytes[2] === 0x33 // 3
    ) {
        const size =
            ((bytes[6] & 0x7f) << 21) |
            ((bytes[7] & 0x7f) << 14) |
            ((bytes[8] & 0x7f) << 7) |
            (bytes[9] & 0x7f)
        offset = 10 + size
    }

    const scanLimit = Math.min(bytes.length, offset + 64 * 1024)
    for (; offset < scanLimit; offset++) {
        const header = parseFrameHeader(bytes, offset)
        if (!header) continue
        const next = offset + header.frameSize
        if (next >= bytes.length || parseFrameHeader(bytes, next)) {
            return offset
        }
    }
    return -1
}

// Returns the offset of the Xing/Info metadata block inside the frame at
// `frameOffset`, or -1 when the frame is a plain audio frame.
function findXingOffset(bytes, frameOffset) {
    const versionBits = (bytes[frameOffset + 1] >> 3) & 0x03
    const channelMode = (bytes[frameOffset + 3] >> 6) & 0x03
    const isMpeg1 = versionBits === 3
    const isMono = channelMode === 3
    const sideInfoSize = isMpeg1 ? (isMono ? 17 : 32) : isMono ? 9 : 17
    const hasCrc = (bytes[frameOffset + 1] & 0x01) === 0

    const offset = frameOffset + 4 + (hasCrc ? 2 : 0) + sideInfoSize
    if (offset + 8 > bytes.length) return -1
    const magic = String.fromCharCode(
        bytes[offset],
        bytes[offset + 1],
        bytes[offset + 2],
        bytes[offset + 3]
    )
    if (magic !== 'Xing' && magic !== 'Info') return -1
    return offset
}

// A VBR file's first frame carries a Xing/Info header that declares the
// total frame and byte count of the stream; after cutting frames off the
// end those counts must be rewritten or players that trust the header will
// still report the original duration.
function updateXingHeader(bytes, firstFrame, frameCount, streamBytes) {
    let offset = findXingOffset(bytes, firstFrame)
    if (offset === -1) return

    const flags = bytes[offset + 7]
    offset += 8
    const writeUint32 = (value) => {
        bytes[offset] = (value >> 24) & 0xff
        bytes[offset + 1] = (value >> 16) & 0xff
        bytes[offset + 2] = (value >> 8) & 0xff
        bytes[offset + 3] = value & 0xff
        offset += 4
    }
    if (flags & 0x01) writeUint32(frameCount - 1) // frames, excluding this one
    if (flags & 0x02) writeUint32(streamBytes)
}

// Cuts the mp3 byte stream so it plays for `endSeconds`. Frames are kept
// while they start before `endSeconds` (granularity is one frame, ~26ms).
// Returns a new Uint8Array, the original array when `endSeconds` is beyond
// the audio duration, or null when the input cannot be parsed.
export function trimMp3Bytes(bytes, endSeconds) {
    if (!bytes || !Number.isFinite(endSeconds) || endSeconds <= 0) return null

    const firstFrame = findFirstFrame(bytes)
    if (firstFrame === -1) return null

    let offset = firstFrame
    let elapsed = 0
    let frameCount = 0
    while (offset < bytes.length) {
        const header = parseFrameHeader(bytes, offset)
        if (!header) {
            // resync: tolerate junk between frames by scanning forward
            const resyncLimit = Math.min(bytes.length, offset + 64 * 1024)
            let next = offset + 1
            while (next < resyncLimit && !parseFrameHeader(bytes, next)) next++
            if (next >= resyncLimit) break
            offset = next
            continue
        }
        if (elapsed >= endSeconds) {
            const trimmed = bytes.slice(0, offset)
            updateXingHeader(
                trimmed,
                firstFrame,
                frameCount,
                offset - firstFrame
            )
            return trimmed
        }
        elapsed += header.frameSeconds
        offset += header.frameSize
        frameCount++
    }
    return bytes
}

// Cuts the mp3 byte stream down to the frames between `startSeconds` and
// `endSeconds`, preserving (and rewriting) a leading Xing/Info header frame
// when one is present. The ID3 tag is dropped since the offsets it points
// at no longer exist after cutting from the front.
// Returns a new Uint8Array, or null when the input cannot be parsed or the
// selection contains no frames.
export function trimMp3BytesRange(bytes, startSeconds, endSeconds) {
    if (!bytes) return null
    if (!Number.isFinite(startSeconds) || !Number.isFinite(endSeconds))
        return null
    const start = Math.max(0, startSeconds)
    if (endSeconds <= start) return null

    const firstFrame = findFirstFrame(bytes)
    if (firstFrame === -1) return null

    let offset = firstFrame
    let infoFrame = null
    const firstHeader = parseFrameHeader(bytes, firstFrame)
    if (firstHeader && findXingOffset(bytes, firstFrame) !== -1) {
        infoFrame = { offset: firstFrame, size: firstHeader.frameSize }
        offset = firstFrame + firstHeader.frameSize
    }

    let elapsed = 0
    let frameCount = 0
    let audioBytes = 0
    const keptFrames = []
    while (offset < bytes.length) {
        const header = parseFrameHeader(bytes, offset)
        if (!header) {
            // resync: tolerate junk between frames by scanning forward
            const resyncLimit = Math.min(bytes.length, offset + 64 * 1024)
            let next = offset + 1
            while (next < resyncLimit && !parseFrameHeader(bytes, next)) next++
            if (next >= resyncLimit) break
            offset = next
            continue
        }
        if (elapsed >= endSeconds) break
        const frameEnd = elapsed + header.frameSeconds
        if (frameEnd > start) {
            const size = Math.min(header.frameSize, bytes.length - offset)
            keptFrames.push({ offset, size })
            audioBytes += size
            frameCount++
        }
        elapsed = frameEnd
        offset += header.frameSize
    }
    if (keptFrames.length === 0) return null

    const infoSize = infoFrame ? infoFrame.size : 0
    const trimmed = new Uint8Array(infoSize + audioBytes)
    let writeOffset = 0
    if (infoFrame) {
        trimmed.set(
            bytes.subarray(
                infoFrame.offset,
                infoFrame.offset + infoFrame.size
            ),
            0
        )
        writeOffset = infoSize
    }
    keptFrames.forEach((frame) => {
        trimmed.set(
            bytes.subarray(frame.offset, frame.offset + frame.size),
            writeOffset
        )
        writeOffset += frame.size
    })
    if (infoFrame) {
        updateXingHeader(trimmed, 0, frameCount + 1, trimmed.length)
    }
    return trimmed
}

const CHUNK = 0x8000

function bytesToBase64(bytes) {
    let bin = ''
    for (let i = 0; i < bytes.length; i += CHUNK) {
        bin += String.fromCharCode.apply(
            null,
            bytes.subarray(i, i + CHUNK)
        )
    }
    return btoa(bin)
}

// Takes an mp3 data URL ("data:audio/mp3;base64,...") and returns the base64
// payload trimmed to `endSeconds`. Falls back to the original, untrimmed
// base64 payload whenever trimming is not possible, so callers can use the
// result unconditionally.
export function trimMp3DataUrl(dataUrl, endSeconds) {
    const base64 = dataUrl.split(',')[1]
    try {
        const bin = atob(base64)
        const bytes = new Uint8Array(bin.length)
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)

        const trimmed = trimMp3Bytes(bytes, endSeconds)
        if (!trimmed) return base64
        if (trimmed.length >= bytes.length) return base64
        return bytesToBase64(trimmed)
    } catch (e) {
        console.error('mp3 trim failed, saving full audio', e)
        return base64
    }
}

// Takes an mp3 data URL and returns a new data URL containing only the audio
// between `startSeconds` and `endSeconds`, or null when trimming fails so
// callers can keep the original audio and report the failure.
export function trimMp3DataUrlRange(dataUrl, startSeconds, endSeconds) {
    try {
        const base64 = dataUrl.split(',')[1]
        const bin = atob(base64)
        const bytes = new Uint8Array(bin.length)
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)

        const trimmed = trimMp3BytesRange(bytes, startSeconds, endSeconds)
        if (!trimmed) return null
        return `data:audio/mpeg;base64,${bytesToBase64(trimmed)}`
    } catch (e) {
        console.error('mp3 range trim failed', e)
        return null
    }
}
